import {createTheme, Theme} from "@mui/material";
import {
  argbFromHex,
  themeFromSourceColor,
  hexFromArgb,
} from "@material/material-color-utilities";
import {
  blue,
  cyan,
  green,
  grey,
  orange,
  purple,
  red,
  teal,
  yellow,
} from "@mui/material/colors";
import {createContext, ReactNode, useContext, useState} from "react";
import {preferDarkMode} from "@util";
import {muiComponentCustomization} from "./mui-component-customization";
import {colorScheme} from "@core/store/slices/appSlice";
import {DEF_PRIMARY_COLOR, DEF_SECONDARY_COLOR} from "@constants";

const typography = {
  fontFamily: [
    "Montserrat",
    "-apple-system",
    "BlinkMacSystemFont",
    '"Segoe UI"',
    "Roboto",
    '"Helvetica Neue"',
    "Arial",
    "sans-serif",
  ].join(","),
};

export type AppTheme = Theme & {
  colors: {
    blue: string;
    red: string;
    yellow: string;
    grey: string;
    orange: string;
    green: string;
    teal: string;
    cyan: string;
    purple: string;
    border: string;
    tertiary: string;
    primaryContainer: string;
    secondaryContainer: string;
  };
  palette: Theme["palette"] & {
    background: Theme["palette"]["background"] & {
      onPaper: string;
    };
  };
};

type ThemeContextType = {
  theme: AppTheme;
  setTheme: (theme: AppTheme) => void;
  onPrimary: string;
  onSecondary: string;
  onBackground: string;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = (): ThemeContextType => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useThemeContext must be used within a ThemeProvider");
  }

  return context;
};

type ThemeProviderProps = {
  children: ReactNode;
};

export const createAppTheme = (
  primaryColor: string,
  secondaryColor: string,
  colorScheme: colorScheme,
): AppTheme => {
  const darkMode =
    colorScheme === "dark" || (colorScheme === "system" && preferDarkMode());

  const md3Theme = themeFromSourceColor(argbFromHex(primaryColor));
  const scheme = darkMode ? md3Theme.schemes.dark : md3Theme.schemes.light;

  const theme = createTheme({
    ...{
      typography,
      palette: {
        mode: darkMode ? "dark" : "light",
        primary: {
          main: hexFromArgb(scheme.primary),
        },
        secondary: {
          main: hexFromArgb(scheme.secondary),
        },
        background: {
          default: hexFromArgb(scheme.surface),
          paper: hexFromArgb(scheme.inverseOnSurface),
        },
      },
      colorSchemes: {light: true, dark: true},
      components: muiComponentCustomization,
    },
  });

  const t = {
    ...theme,
    colors: {
      blue: darkMode ? blue[900] : blue[400],
      red: darkMode ? red[300] : red[400],
      orange: darkMode ? orange[500] : orange[200],
      yellow: darkMode ? yellow[800] : yellow[600],
      green: darkMode ? green[500] : green[400],
      cyan: darkMode ? cyan[900] : cyan[400],
      teal: darkMode ? teal[900] : teal[400],
      purple: darkMode ? purple[900] : purple[400],
      grey: darkMode ? grey[800] : grey[100],
      border: darkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
      primaryContainer: hexFromArgb(scheme.primaryContainer),
      secondaryContainer: hexFromArgb(scheme.secondaryContainer),
      tertiary: secondaryColor,
    },
    palette: {
      ...theme.palette,
      background: {
        ...theme.palette.background,
        onPaper: hexFromArgb(scheme.surfaceVariant),
      },
    },
  } as AppTheme;

  return t;
};

const defaultTheme = createAppTheme(
  DEF_PRIMARY_COLOR,
  DEF_SECONDARY_COLOR,
  "system",
);

export const ThemeProvider: React.FC<ThemeProviderProps> = ({children}) => {
  const [theme, setTheme] = useState<AppTheme>(defaultTheme);

  const onPrimary = theme.palette.getContrastText(theme.palette.primary.dark);
  const onBackground = theme.palette.getContrastText(
    theme.palette.background.default,
  );
  const onSecondary = theme.palette.getContrastText(
    theme.palette.secondary.dark,
  );

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        onPrimary,
        onSecondary,
        onBackground,
      }}>
      {children}
    </ThemeContext.Provider>
  );
};

export {defaultTheme};
